import React, {
  useCallback, useMemo, useState,
} from 'react';
import {
  Check, ChevronRight, Globe2, MapPin, Plus, X,
} from 'lucide-react';

import { useXomicsAuth } from './AuthContext';
import { loadOfficeShipping, saveOfficeShipping } from './api';
import { ISO_COUNTRIES, countryName } from './countries';
import {
  Empty, Failed, Head, Loading, SearchField, useLeave,
} from './parts';
import { Switch } from './ProductOptions';
import { amountOf, shownAmount } from './storeAmounts';
import { OfficeNamed, StoreOfficeLine, useStoreOffice } from './StoreOffice';
import { errorMessage } from './ui';
import { useLiveResource } from './useLive';
import { tx } from './i18n';
import './products.css';

// Shipping prices: what one office asks for sending an order.
//
//   International: one price in each currency. It is what every country is
//   asked, unless the country has a price of its own.
//   Countries with their own price: any country can be added, with a price in
//   that country's currency. An office adds the countries it wants priced
//   apart, also ones it is not responsible for yet.
//
// The page is a list. A row opens its form, and each form has one Save. A
// price is an amount or the Free switch; nobody types a zero.

function money(amountMinor, currency) {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency.code }).format(amountMinor / (10 ** currency.minorUnit));
}
// What a field starts from: free where nothing is asked, else the amount kept.
const cellOf = (amountMinor, currency) => (
  amountMinor === 0 ? { free: true, text: '' } : { free: false, text: shownAmount(amountMinor, currency) });
// What a field stands for, in minor units: 0 when it is free, null when it is
// empty, undefined when what is typed is not an amount.
const amountIn = (cell, currency) => (cell.free ? 0 : amountOf(cell.text, currency.minorUnit));

// A price as a list says it: the word where nothing is asked, else the amount.
function Asked({ amountMinor, currency }) {
  if (amountMinor === 0) return <b className="xo-ship-free is-small">{tx('ui.store.free')}</b>;
  return <span className="xo-pc-number">{money(amountMinor, currency)}</span>;
}

// One price in a form: its field (or the word Free in the field's place)
// beside the switch that says it is free.
function Price({
  currency, label, cell, marked, disabled, onChange,
}) {
  return (
    <span className="xo-ship-price">
      {cell.free ? <b className="xo-ship-free">{tx('ui.store.free')}</b> : (
        <label className={`xo-ps-amount${marked ? ' is-marked' : ''}`}>
          <b>{currency.code}</b>
          <input type="text" inputMode="decimal" value={cell.text} disabled={disabled} aria-label={label}
            aria-invalid={marked || undefined}
            onChange={(event) => onChange({ free: false, text: event.target.value })}
            // A zero typed is the same as free, and is shown as the word.
            onBlur={() => { if (amountOf(cell.text, currency.minorUnit) === 0) onChange({ free: true, text: '' }); }} />
        </label>
      )}
      <span className="xo-ship-switch">
        <Switch on={cell.free} disabled={disabled} label={`${label}: ${tx('ui.store.free')}`}
          onChange={(free) => onChange({ free, text: '' })} />
        <small>{tx('ui.store.free')}</small>
      </span>
    </span>
  );
}

// The frame every form of this page opens in.
function Form({
  title, icon: Icon, office, busy, error, mayManage, onLeave, onSubmit, children, aside = null, narrow = true,
}) {
  return (
    <div className="xo-product-modal-backdrop" role="presentation" onMouseDown={onLeave}>
      <form className={`xo-pc-form${narrow ? ' is-narrow' : ''}`} role="dialog" aria-modal="true" aria-labelledby="xo-ship-form-title"
        onSubmit={onSubmit} onMouseDown={(event) => event.stopPropagation()}>
        <header className="xo-pc-form-head">
          <span className="xo-pc-thumb is-icon"><Icon aria-hidden="true" /></span>
          <div>
            <h2 id="xo-ship-form-title">{title}</h2>
            <OfficeNamed office={office} />
          </div>
          <button type="button" className="xo-icon-btn" aria-label={tx("ui.products.close_bbfa773e")} disabled={busy} onClick={onLeave}>
            <X aria-hidden="true" /></button>
        </header>
        {children}
        {onSubmit && (
          <footer className="xo-pc-form-foot">
            {error ? <p className="xo-alert t-bad" role="alert">{error}</p> : (aside ?? <span />)}
            <button type="button" className="xo-btn-2" disabled={busy} onClick={onLeave}>
              {mayManage ? tx('ui.common.cancel') : tx("ui.products.close_bbfa773e")}</button>
            {mayManage && <button type="submit" className="xo-btn" disabled={busy}>
              {busy ? tx('ui.products.settings.saving') : tx('ui.products.settings.save')}</button>}
          </footer>
        )}
      </form>
    </div>
  );
}

// The international price, in every currency. Nothing changes until Save, and
// Save keeps all of it or none.
function InternationalForm({
  office, shipping, currencies, mayManage, onClose, onSaved,
}) {
  // The currencies the office's visitors pay in first, its own before the rest.
  const rows = useMemo(() => [...shipping.international].sort((left, right) => (
    Number(right.isNeeded) - Number(left.isNeeded) || Number(right.currency === office.homeCurrency) - Number(left.currency === office.homeCurrency)
    || left.currencyName.localeCompare(right.currencyName))), [office.homeCurrency, shipping]);
  const [prices, setPrices] = useState(() => Object.fromEntries(rows.map((entry) => [entry.currency, cellOf(entry.amountMinor, currencies.get(entry.currency))])));
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [marked, setMarked] = useState([]);
  const leave = useLeave(busy, touched, onClose);

  async function save(event) {
    event.preventDefault();
    setError('');
    const changed = [];
    const wrong = [];
    const empty = [];
    for (const entry of rows) {
      const amount = amountIn(prices[entry.currency], currencies.get(entry.currency));
      if (amount === undefined) wrong.push(entry.currency);
      // A currency the office's visitors pay in has a price: it is free by the switch, not by being left empty.
      else if (amount === null && entry.isNeeded) empty.push(entry.currency);
      else if (amount !== entry.amountMinor) changed.push({ currency: entry.currency, amountMinor: amount });
    }
    if (wrong.length > 0 || empty.length > 0) {
      setMarked(wrong.length > 0 ? wrong : empty);
      setError(wrong.length > 0 ? tx('ui.store.error.shipping_amount', { names: wrong.join(', ') })
        : tx('ui.store.error.needs_shipping', { currencies: empty.join(', ') }));
      return;
    }
    if (changed.length === 0) { onClose(); return; }
    setBusy(true);
    try {
      await saveOfficeShipping(office.id, { international: changed, countries: [] });
      onSaved(tx('ui.store.saved', { name: tx('ui.store.international') }));
    } catch (caught) {
      setError(errorMessage(caught));
      setBusy(false);
    }
  }

  const disabled = !mayManage || busy;
  return (
    <Form title={tx('ui.store.international')} icon={Globe2} office={office} busy={busy} error={error} mayManage={mayManage}
      onLeave={leave} onSubmit={save}>
      <div className="xo-pc-form-body">
        <p className="xo-ship-what">{tx('ui.store.international_what')}</p>
        <div className="xo-ship-lines">
          {rows.map((entry) => (
            <div key={entry.currency} role="group" aria-label={entry.currencyName} className={`xo-ship-line${entry.isNeeded ? '' : ' is-spare'}`}>
              <strong>{entry.currencyName}</strong>
              <Price currency={currencies.get(entry.currency)} cell={prices[entry.currency]} disabled={disabled}
                label={tx('ui.store.international_in', { currency: entry.currencyName })} marked={marked.includes(entry.currency)}
                onChange={(cell) => { setTouched(true); setPrices((current) => ({ ...current, [entry.currency]: cell })); }} />
            </div>
          ))}
        </div>
      </div>
    </Form>
  );
}

// Every country, to choose the one that gets a price of its own. A country
// that already has one cannot be chosen again.
function CountryPicker({ office, taken, onPick, onClose }) {
  const [query, setQuery] = useState('');
  const leave = useLeave(false, false, onClose);
  const wanted = query.trim().toLowerCase();
  const shown = ISO_COUNTRIES.filter((country) => wanted === '' || country.name.toLowerCase().includes(wanted));
  return (
    <Form title={tx('ui.store.add_country')} icon={MapPin} office={office} busy={false} error="" mayManage onLeave={leave} onSubmit={null}>
      <div className="xo-pc-form-body xo-ship-pick">
        <SearchField value={query} onChange={setQuery} placeholder={tx('ui.store.search_countries')} autoFocus />
        <div className="xo-ship-countries" role="listbox" aria-label={tx('ui.store.add_country')}>
          {shown.map((country) => (
            <button type="button" key={country.code} role="option" aria-selected="false" disabled={taken.has(country.code)}
              onClick={() => onPick(country.code)}>
              <span>{country.name}</span>
              {taken.has(country.code) && <small>{tx('ui.store.has_own_price')}</small>}
            </button>
          ))}
          {shown.length === 0 && <p>{tx('ui.store.no_such_country')}</p>}
        </div>
      </div>
    </Form>
  );
}

// One country's own price. `kept` is the amount it has now, null for a country
// that is being added.
function CountryForm({
  office, countryCode, currency, kept, mayManage, onClose, onSaved,
}) {
  const name = countryName(countryCode);
  const [cell, setCell] = useState(() => (kept === null ? { free: false, text: '' } : cellOf(kept, currency)));
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [marked, setMarked] = useState(false);
  const leave = useLeave(busy, touched, onClose);

  async function send(amountMinor, said) {
    setBusy(true);
    setError('');
    try {
      await saveOfficeShipping(office.id, { international: [], countries: [{ countryCode, amountMinor }] });
      onSaved(said);
    } catch (caught) {
      setError(errorMessage(caught));
      setBusy(false);
    }
  }

  function save(event) {
    event.preventDefault();
    const amount = amountIn(cell, currency);
    if (amount === undefined || amount === null) {
      setMarked(true);
      setError(amount === undefined ? tx('ui.store.error.shipping_amount', { names: name }) : tx('ui.store.error.country_needs_price', { countries: name }));
      return undefined;
    }
    if (amount === kept) { onClose(); return undefined; }
    return send(amount, tx('ui.store.saved', { name }));
  }

  // Taking a country's price off puts it back on the international one.
  function remove() {
    if (!window.confirm(tx('ui.store.remove_country_confirm', { country: name }))) return;
    send(null, tx('ui.store.country_removed', { country: name }));
  }

  return (
    <Form title={name} icon={MapPin} office={office} busy={busy} error={error} mayManage={mayManage} onLeave={leave} onSubmit={save}
      aside={mayManage && kept !== null ? (
        <button type="button" className="xo-link xo-ship-remove" disabled={busy} onClick={remove}>{tx('ui.store.remove_own_price')}</button>
      ) : null}>
      <div className="xo-pc-form-body">
        <div className="xo-ship-lines">
          <div role="group" aria-label={name} className="xo-ship-line">
            <strong>{tx('ui.store.shipping_price')}</strong>
            <Price currency={currency} cell={cell} disabled={!mayManage || busy} label={tx('ui.store.shipping_for', { country: name })} marked={marked}
              onChange={(next) => { setTouched(true); setMarked(false); setCell(next); }} />
          </div>
        </div>
      </div>
    </Form>
  );
}

export default function XomicsShipping() {
  const { permissions } = useXomicsAuth();
  const mayManage = permissions.includes('PRODUCT_MANAGE');
  const store = useStoreOffice();
  const office = store.office;
  const officeId = office?.id ?? null;
  const shipping = useLiveResource(['products'], useCallback(() => loadOfficeShipping(officeId), [officeId]), { enabled: officeId !== null });
  // What is open over the page: the international form, the list of countries
  // to add one from, or one country's form.
  const [open, setOpen] = useState(null);
  const [notice, setNotice] = useState('');

  const data = shipping.data;
  // A currency as a field needs it: its code and how many decimals it has.
  const currencies = useMemo(() => new Map((data?.international ?? []).map((entry) => (
    [entry.currency, { code: entry.currency, name: entry.currencyName, minorUnit: entry.minorUnit }]))), [data]);

  // A country is added from the head, once the office's prices are read.
  const read = office !== null && data !== null && data.officeId === officeId;
  const head = (
    <Head title={tx('ui.nav.shipping_prices')} under={<StoreOfficeLine store={store} />}>
      {read && mayManage && (
        <button type="button" className="xo-btn" onClick={() => { setNotice(''); setOpen({ form: 'add' }); }}>
          <Plus aria-hidden="true" />{tx('ui.store.add_country')}</button>
      )}
    </Head>
  );

  if (store.error) return <Failed message={store.error} onRetry={store.refresh} />;
  if (store.loading) return <Loading label={tx('ui.store.reading_shipping')} />;
  // An account whose location keeps no store has nothing to set here.
  if (office === null) return <div className="xo-products-page xo-pc-page">{head}<Empty title={tx('ui.store.no_office')} /></div>;
  if (shipping.error) return <Failed message={shipping.error} onRetry={shipping.refresh} />;
  // Another office was chosen: its prices are shown once they are read, never the last one's under its name.
  if (data === null || data.officeId !== officeId) return <Loading label={tx('ui.store.reading_shipping')} />;

  // The currency a country's store sells in.
  const currencyOf = (countryCode) => currencies.get(data.countryCurrencies[countryCode] ?? data.fallbackCurrency);
  // The international price as the list says it: in the office's own currency
  // where it has one, else in the first its visitors pay in.
  const priced = data.international.filter((entry) => entry.amountMinor !== null);
  const shown = priced.find((entry) => entry.currency === office.homeCurrency) ?? priced.find((entry) => entry.isNeeded) ?? priced[0] ?? null;
  const countries = [...data.countries].sort((left, right) => countryName(left.countryCode).localeCompare(countryName(right.countryCode)));
  const saved = (said) => { setOpen(null); setNotice(said); shipping.refresh(); };
  const close = () => setOpen(null);

  return (
    <div className="xo-products-page xo-pc-page">
      {head}
      {notice && <p className="xo-alert t-ok" role="status"><Check aria-hidden="true" />{notice}</p>}
      <div className="xo-ship">
        <div className="xo-pc-list is-shipping">
          <button type="button" className="xo-pc-row is-on" onClick={() => { setNotice(''); setOpen({ form: 'international' }); }}>
            <span className="xo-pc-name">
              <span className="xo-pc-thumb is-icon"><Globe2 aria-hidden="true" /></span>
              <span><strong>{tx('ui.store.international')}</strong><small>{tx('ui.store.international_what')}</small></span>
            </span>
            {shown ? <Asked amountMinor={shown.amountMinor} currency={currencies.get(shown.currency)} /> : <span />}
            <ChevronRight aria-hidden="true" />
          </button>
        </div>

        <section className="xo-ship-part">
          <h2>{tx('ui.store.own_price_countries')}</h2>
          {countries.length === 0 ? <p className="xo-ship-none">{tx('ui.store.no_own_price')}</p> : (
            <div className="xo-pc-list is-shipping">
              {countries.map((entry) => (
                <button type="button" key={entry.countryCode} className="xo-pc-row is-on"
                  onClick={() => { setNotice(''); setOpen({ form: 'country', countryCode: entry.countryCode }); }}>
                  <span className="xo-pc-name"><span><strong>{countryName(entry.countryCode)}</strong></span></span>
                  <Asked amountMinor={entry.amountMinor} currency={currencies.get(entry.currency)} />
                  <ChevronRight aria-hidden="true" />
                </button>
              ))}
            </div>
          )}
        </section>
      </div>

      {open?.form === 'international' && (
        <InternationalForm office={office} shipping={data} currencies={currencies} mayManage={mayManage} onClose={close} onSaved={saved} />
      )}
      {open?.form === 'add' && (
        <CountryPicker office={office} taken={new Set(data.countries.map((entry) => entry.countryCode))} onClose={close}
          onPick={(countryCode) => setOpen({ form: 'country', countryCode })} />
      )}
      {open?.form === 'country' && (
        <CountryForm key={open.countryCode} office={office} countryCode={open.countryCode} currency={currencyOf(open.countryCode)}
          kept={data.countries.find((entry) => entry.countryCode === open.countryCode)?.amountMinor ?? null}
          mayManage={mayManage} onClose={close} onSaved={saved} />
      )}
    </div>
  );
}
